import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { BarChart3, Plug, Edit, Copy, Power, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { VideoCardUnified, VideoCardAction, VideoCardBadge } from '@/components/shared/VideoCardUnified';
import { VideoGrid } from '@/components/shared/VideoGrid';
import { NewCaptureCard } from './NewCaptureCard';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { vsleadsService } from '@/services/vsleadsService';

export interface Strategy {
  id: string;
  videoTitle: string;
  tipo: 'captura-dados' | 'captura-senha';
  createdAt: Date;
  status: 'active' | 'inactive';
  videoThumbnail?: string;
  videoId?: string;
  totalViews?: number;
}

interface VSLeadsStrategyListProps {
  strategies: Strategy[];
  type: 'captura-dados' | 'captura-senha';
  onUpdate?: (id: string, updates: Partial<Strategy>) => void;
  onDelete?: (id: string) => void;
  onNavigateToReports?: (videoId: string) => void;
  onNavigateToIntegrations?: () => void;
  onCreateNew?: () => void;
}

export function VSLeadsStrategyList({
  strategies,
  type,
  onUpdate,
  onDelete,
  onNavigateToReports,
  onNavigateToIntegrations,
  onCreateNew
}: VSLeadsStrategyListProps) {
  useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingStrategy, setPendingStrategy] = useState<Strategy | null>(null);

  const handleToggleActive = (strategyId: string) => {
    const strategy = strategies.find(s => s.id === strategyId);
    if (!strategy || !onUpdate) return;

    const newStatus = strategy.status === 'active' ? 'inactive' : 'active';
    onUpdate(strategyId, { status: newStatus });

    toast({
      title: newStatus === 'active' ? translateInterfaceText("Estratégia ativada") : translateInterfaceText("Estratégia desativada"),
      description: translateInterfaceText("A estratégia foi {value0}.", { value0: (newStatus === 'active' ? 'ativada' : 'desativada') }),
    });
  };

  const handleDisableCapture = async (strategy: Strategy) => {
    if (!strategy.videoId) {
      toast({
        title: translateInterfaceText("Erro"),
        description: translateInterfaceText("ID do vídeo não encontrado."),
        variant: "destructive",
      });
      return;
    }

    try {
      // Dados para desativar a captura
      const disableData = {
        captureAuxText: "",
        captureAuxTextColor: "",
        captureBackgroundColor: "",
        captureButtonColor: "",
        captureButtonText: "",
        captureButtonTextColor: "",
        captureInputColor: "",
        captureTitle: "",
        captureTitleColor: "",
        checkedCaptureTimer: null,
        checkedCaptureVertical: null,
        haveBackgroundColored: null,
        haveCaptureAuxText: null,
        haveCaptureBigTitle: null,
        haveForm: false,
        haveInputEmail: null,
        haveInputName: null,
        haveInputWhatsapp: null,
        haveTime: null,
        password: "",
        skipLead: null,
        typeForm: ""
      };

      // Chama a API apropriada baseado no tipo de captura
      if (type === 'captura-dados') {
        await vsleadsService.updateDataCapture(strategy.videoId, disableData);
      } else {
        await vsleadsService.updatePasswordCapture(strategy.videoId, disableData);
      }

      // Atualiza o status local
      if (onUpdate) {
        onUpdate(strategy.id, { status: 'inactive' });
      }

      toast({
        title: translateInterfaceText("Captura desativada"),
        description: translateInterfaceText("A captura foi desativada com sucesso."),
      });
    } catch (error) {
      console.error('Erro ao desativar captura:', error);
      toast({
        title: translateInterfaceText("Erro"),
        description: translateInterfaceText("Não foi possível desativar a captura."),
        variant: "destructive",
      });
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat(getInterfaceLanguage(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getBadge = (status: Strategy['status']): VideoCardBadge => {
    return status === 'active' 
      ? { label: translateInterfaceText("Ativo"), variant: 'active' }
      : { label: translateInterfaceText("Rascunho"), variant: 'draft' };
  };

  const getActions = (strategy: Strategy): VideoCardAction[] => {
    const actions: VideoCardAction[] = [
      {
        label: translateInterfaceText("Copiar código"),
        icon: <Copy className="h-4 w-4" />,
        onClick: () => {
          setSelectedStrategy(strategy);
          setEmbedModalOpen(true);
        }
      },
      {
        label: translateInterfaceText("Editar captura"),
        icon: <Edit className="h-4 w-4" />,
        onClick: () => {
          const tipoParam = type === 'captura-dados' ? 'captura-dados' : 'captura-senha';
          navigate(`/vsleads/editar/${strategy.id}?tipo=${tipoParam}`);
        }
      },
      // {
      //   label: 'Ver relatórios',
      //   icon: <BarChart3 className="h-4 w-4" />,
      //   onClick: () => {
      //     if (onNavigateToReports) {
      //       onNavigateToReports(strategy.videoId || strategy.id);
      //     }
      //   }
      // },
      {
        label: translateInterfaceText("Integrações"),
        icon: <Plug className="h-4 w-4" />,
        onClick: () => {
          if (onNavigateToIntegrations) {
            onNavigateToIntegrations();
          }
        }
      }
    ];

    // Adiciona "Desativar" apenas se a estratégia estiver ativa
    if (strategy.status === 'active') {
      actions.push({
        label: translateInterfaceText("Desativar"),
        icon: <Power className="h-4 w-4" />,
        onClick: () => { setPendingStrategy(strategy); setDisableDialogOpen(true); },
        variant: 'destructive'
      });
    }

    return actions;
  };

  return (
    <>
      <VideoGrid>
        {/* Card de Nova Captura SEMPRE PRIMEIRO */}
        <NewCaptureCard
          onCreateNew={onCreateNew || (() => {})}
          type={type === 'captura-dados' ? 'email' : 'password'}
        />

        {/* Cards das Estratégias Existentes */}
        {strategies.map((strategy) => (
        <VideoCardUnified
          key={strategy.id}
          id={strategy.id}
          variant="list"
          title={strategy.videoTitle}
          thumbnail={strategy.videoThumbnail || 'https://placehold.co/600x400/purple/white?text=Video'}
          badge={getBadge(strategy.status)}
          className="shadow-[0_4px_14px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.10)]"
          showToggle={true}
          toggleValue={strategy.status === 'active'}
          onToggleChange={() => handleToggleActive(strategy.id)}
          footerDate={formatDate(strategy.createdAt)}
          onClick={() => {
            const tipoParam = type === 'captura-dados' ? 'captura-dados' : 'captura-senha';
            navigate(`/vsleads/editar/${strategy.id}?tipo=${tipoParam}`);
          }}
          actions={getActions(strategy)}
        />
        ))}
      </VideoGrid>

      {/* Modal de Copiar Código */}
      {selectedStrategy && selectedStrategy.videoId && (
        <CopyCodeModal
          isOpen={embedModalOpen}
          onClose={() => {
            setEmbedModalOpen(false);
            setSelectedStrategy(null);
          }}
          entityType="video"
          entityId={selectedStrategy.videoId}
          entityName={selectedStrategy.videoTitle}
          userId={user?.id_user}
          videoOrigin="ORIGINAL"
          hideJsTab={false}
        />
      )}

      {/* Modal de confirmação — Desativar */}
      <AlertDialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <AlertDialogContent className="!w-[40vh] md:w-[35vh] rounded-xl p-6">
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Desativar captura")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Tem certeza que deseja desativar a captura de \"")}{pendingStrategy?.videoTitle}{translateInterfaceText("\"? Ela deixará de funcionar imediatamente.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors">
              {translateInterfaceText("Cancelar")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (pendingStrategy) handleDisableCapture(pendingStrategy); setDisableDialogOpen(false); setPendingStrategy(null); }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {translateInterfaceText("Desativar")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
